import {
  ContentfulBusinessTemplatePage,
  getContentfulBusinessTemplatePage,
} from 'api/contentful/getContentfulBusinessTemplatePage'
import { BusinessArticleCards } from 'components/business/BusinessArticleCards'
import { BusinessHero } from 'components/business/BusinessHero'
import { BusinessRegister } from 'components/business/BusinessRegister'
import { BusinessStatistics } from 'components/business/BusinessStatistics'
import { Button } from 'components/button/Button'
import { ContentfulContent } from 'components/content/contentfulContent/ContentfulContent'
import { ThemedTextImageSection } from 'components/content/themedTextImageSection/ThemedTextImageSection'
import { DynamicFooter } from 'components/estatePlanning/landing/EstatePlanningLP/dynamicImports'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { ContentfulImage } from 'components/Image'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Stack } from 'components/Stack'
import { GetStaticPaths, GetStaticProps } from 'next'
import React from 'react'
import { REVALIDATE_SECS } from 'utils/constants'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { getIntlProps } from 'utils/i18n/getIntlProps'

const VALID_BUSINESS_PAGES = [
  'employee-benefits',
  'insurance-provider',
  'financial-institutions',
  'member-benefits',
] as const

type BusinessPageType = (typeof VALID_BUSINESS_PAGES)[number]

type BusinessPageProps = {
  businessPageTemplate: ContentfulBusinessTemplatePage
}

export default function BusinessPage(props: BusinessPageProps) {
  const bt = props.businessPageTemplate
  return (
    <Layout
      header={<TopNavigationLandingPage variant="estate-planning" noSpacingBelow />}
      footer={<DynamicFooter variant="estate-planning" />}
    >
      <PageTitle title={bt.heroTitle} />
      <PageMeta description={bt.heroDescription} image={bt?.heroImage?.url} />

      <Stack gap={['2rem', '6rem']}>
        <BusinessHero
          title={bt.heroTitle}
          description={bt.heroDescription}
          image={bt?.heroImage?.url}
          cta={bt.heroCta}
          ctaDescription={bt.heroTextBelowCta}
        />

        <BusinessStatistics
          statisticsTitle={bt.statisticsTitle}
          statistics={bt.statisticsCollection?.items}
        />

        {bt.pageSections?.map((ps, idx) => (
          <ThemedTextImageSection
            key={idx.toString()}
            caption={ps?.caption}
            title={ps?.title}
            imagePlacement={idx % 2 === 0 ? 'left' : 'right'}
            image={<ContentfulImage url={ps?.image?.url} />}
          >
            <ContentfulContent data={ps?.description} />

            <Button
              type="button"
              size="large"
              css={{ width: 'min-content !important' }}
              renderAsButton
              href="#contact"
            >
              {ps.primaryButtonLabel}
            </Button>
          </ThemedTextImageSection>
        ))}

        <BusinessArticleCards articles={bt.articles ?? []} title={bt?.articleTitle} />
        {bt.faqListCollection?.items && bt.faqListCollection?.items?.length > 0 && (
          <FaqTwoColumns
            content={{
              items: bt.faqListCollection?.items.map((item) => ({
                question: item.question,
                answer: item.answer,
                sys: {
                  id: item.question,
                },
              })),
            }}
            className="p-0"
          />
        )}
        <BusinessRegister
          caption={bt.businessRegisterCaption}
          title={bt.businessRegisterTitle}
          description={bt.businessRegisterDescription}
        />
      </Stack>
    </Layout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: VALID_BUSINESS_PAGES.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<BusinessPageProps> = async (ctx) => {
  const { slug } = ctx.params as { slug: BusinessPageType }

  if (!VALID_BUSINESS_PAGES.includes(slug)) {
    return { notFound: true }
  }

  const businessPageTemplate = await getContentfulBusinessTemplatePage(slug)

  if (!businessPageTemplate) {
    return { notFound: true }
  }

  const props: BusinessPageProps = {
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['auth', 'business', 'security'])),
    businessPageTemplate,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
